/**
 * BrandedLoader — a "please wait" indicator built around the tenant's logo
 * (white-label aware). The logo sits inside a rotating accent ring and gently
 * pulses. Two modes:
 *   • overlay — absolute, semi-opaque backdrop over its (relative) container,
 *     for maps / panels that are still loading data.
 *   • block   — centred in flow, for page-level Suspense / empty states.
 */
import { brandingAssetUrl } from "@/api/branding";
import { useBrandingStore } from "@/store/brandingStore";

export function BrandedLoader({
  overlay = false,
  label = "Please wait…",
  size = 60,
  className = "",
}: {
  overlay?: boolean;
  label?: string | null;
  size?: number;
  className?: string;
}) {
  const branding = useBrandingStore((s) => s.branding);
  const logo = brandingAssetUrl(branding?.logo_url) ?? "/logo.png";

  const inner = (
    <div className="flex flex-col items-center gap-3" role="status" aria-live="polite">
      <div className="relative" style={{ width: size, height: size }}>
        {/* Rotating accent ring */}
        <div
          className="absolute inset-0 animate-spin rounded-full border-[3px] border-primary/15 border-t-primary"
          style={{ animationDuration: "0.9s" }}
        />
        {/* Pulsing logo */}
        <img
          src={logo}
          alt=""
          className="absolute rounded-full object-contain"
          style={{
            inset: size * 0.16,
            width: size * 0.68,
            height: size * 0.68,
            animation: "mppiPulse 1.4s ease-in-out infinite",
          }}
        />
      </div>
      {label && (
        <p className="text-xs font-medium uppercase tracking-wider text-neutral-500">{label}</p>
      )}
      <style>{`@keyframes mppiPulse{0%,100%{opacity:.55;transform:scale(.94)}50%{opacity:1;transform:scale(1)}}`}</style>
      <span className="sr-only">Loading</span>
    </div>
  );

  if (overlay) {
    return (
      <div
        className={`absolute inset-0 z-[600] flex items-center justify-center bg-white/65 backdrop-blur-[2px] ${className}`}
      >
        {inner}
      </div>
    );
  }
  return (
    <div className={`flex min-h-[96px] w-full items-center justify-center py-6 ${className}`}>
      {inner}
    </div>
  );
}

export default BrandedLoader;
